// src/pages/Register.js
// New customer registration — collects name, email, and password.
// On success, automatically logs the customer in and redirects to /account.

import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import usePageTitle from '../hooks/usePageTitle';

const Register = () => {
    usePageTitle('Create Account', 'Create a MAISON D\'OUD account to track orders and save your favourite scents.');

    const { register, authLoading } = useAuth();
    const navigate = useNavigate();

    // Registration form fields
    const [formData, setFormData] = useState({
        firstName: '',
        lastName: '',
        email: '',
        password: '',
        confirmPassword: '',
    });
    const [error, setError] = useState('');

    // Update a single field and clear any error
    const handleChange = (e) => {
        setFormData(prev => ({ ...prev, [e.target.name]: e.target.value }));
        setError('');
    };

    const handleSubmit = async (e) => {
        e.preventDefault();

        // Client-side validation before hitting the API
        if (formData.password !== formData.confirmPassword) {
            setError('Passwords do not match.');
            return;
        }
        if (formData.password.length < 8) {
            setError('Password must be at least 8 characters.');
            return;
        }

        // Register via Shopify — on success, AuthContext auto-logs in
        const result = await register(
            formData.firstName,
            formData.lastName,
            formData.email,
            formData.password
        );
        if (result.error) {
            setError(result.error);
        } else {
            navigate('/account');
        }
    };

    return (
        <div className="bg-background min-h-screen pt-32 pb-24 px-6 transition-colors duration-300 flex items-center justify-center">
            <div className="w-full max-w-md">
                <div className="text-center mb-10">
                    <h1 className="text-4xl font-light text-foreground tracking-wide mb-2">Create Account</h1>
                    <p className="text-muted-foreground text-sm">Join MAISON D'OUD and track your orders</p>
                </div>

                <div className="bg-card rounded-2xl p-8 border border-border">

                    {/* Validation or API error message */}
                    {error && (
                        <div className="mb-6 p-4 bg-red-500/10 border border-red-500/20 rounded-lg">
                            <p className="text-red-400 text-sm">{error}</p>
                        </div>
                    )}

                    <form onSubmit={handleSubmit} className="space-y-5">

                        {/* Name fields side by side */}
                        <div className="grid grid-cols-2 gap-4">
                            <div>
                                <label className="block text-muted-foreground text-sm mb-2">First Name</label>
                                <input
                                    type="text"
                                    name="firstName"
                                    value={formData.firstName}
                                    onChange={handleChange}
                                    required
                                    className="w-full bg-background border border-border rounded-lg px-4 py-3 text-foreground placeholder:text-muted-foreground focus:outline-none focus:border-[#CEAC8F]/50 transition-colors"
                                    placeholder="John"
                                />
                            </div>
                            <div>
                                <label className="block text-muted-foreground text-sm mb-2">Last Name</label>
                                <input
                                    type="text"
                                    name="lastName"
                                    value={formData.lastName}
                                    onChange={handleChange}
                                    required
                                    className="w-full bg-background border border-border rounded-lg px-4 py-3 text-foreground placeholder:text-muted-foreground focus:outline-none focus:border-[#CEAC8F]/50 transition-colors"
                                    placeholder="Doe"
                                />
                            </div>
                        </div>

                        <div>
                            <label className="block text-muted-foreground text-sm mb-2">Email Address</label>
                            <input
                                type="email"
                                name="email"
                                value={formData.email}
                                onChange={handleChange}
                                required
                                className="w-full bg-background border border-border rounded-lg px-4 py-3 text-foreground placeholder:text-muted-foreground focus:outline-none focus:border-[#CEAC8F]/50 transition-colors"
                                placeholder="john@example.com"
                            />
                        </div>

                        <div>
                            <label className="block text-muted-foreground text-sm mb-2">Password</label>
                            <input
                                type="password"
                                name="password"
                                value={formData.password}
                                onChange={handleChange}
                                required
                                className="w-full bg-background border border-border rounded-lg px-4 py-3 text-foreground placeholder:text-muted-foreground focus:outline-none focus:border-[#CEAC8F]/50 transition-colors"
                                placeholder="Min. 8 characters"
                            />
                        </div>

                        <div>
                            <label className="block text-muted-foreground text-sm mb-2">Confirm Password</label>
                            <input
                                type="password"
                                name="confirmPassword"
                                value={formData.confirmPassword}
                                onChange={handleChange}
                                required
                                className="w-full bg-background border border-border rounded-lg px-4 py-3 text-foreground placeholder:text-muted-foreground focus:outline-none focus:border-[#CEAC8F]/50 transition-colors"
                                placeholder="••••••••"
                            />
                        </div>

                        <button
                            type="submit"
                            disabled={authLoading}
                            className="w-full bg-[#CEAC8F] text-black py-4 rounded-lg font-medium hover:bg-[#d4b896] transition-colors disabled:opacity-60"
                        >
                            {authLoading ? 'Creating account...' : 'Create Account'}
                        </button>
                    </form>

                    {/* Link back to login */}
                    <p className="text-center text-muted-foreground text-sm mt-6">
                        Already have an account?{' '}
                        <Link to="/login" className="text-[#CEAC8F] hover:underline">
                            Sign in
                        </Link>
                    </p>
                </div>
            </div>
        </div>
    );
};

export default Register;